import axios from "axios";
import { RiskStoryEntity, RiskStoryModel } from "./risk-story.types";
import {
  mapRiskStoryResponse,
  mapRiskStorySummary,
  mapAggregationResponse,
} from "./risk-story.helpers";

// NOTE: endpoint paths are the single place to adjust against live data.
// The Risk Story API is the existing event-risk-stories endpoint (param: eventId),
// enhanced by ADO-24688 to return the entity context (entityId, virtualLabelId, ...).
const RISK_STORY_URL = (internalEventId: string) =>
  `/extproxy/integrations/status/event-risk-stories?eventId=${internalEventId}`;
// `labelId` is required and carries the entity's virtualLabelId value (param name
// is labelId, not virtualLabelId). `internalEntityIds` is an array param: the
// backend only parses it as an array when the key is repeated, so a single id must
// be passed twice (per the integrations API owner). The path includes the same
// `status/` segment as event-risk-stories above.
const AGGREGATION_URL = (internalEntityId: string, virtualLabelId: string) =>
  `/extproxy/integrations/status/effective-trust-familiarity-aggregations` +
  `?labelId=${virtualLabelId}` +
  `&internalEntityIds=${internalEntityId}&internalEntityIds=${internalEntityId}`;

// NOTE: these are deliberately plain GETs with no custom headers. A custom
// request header (e.g. x-suppress-error-toast) is non-safelisted and forces a
// CORS preflight (OPTIONS); endpoints whose CORS config doesn't allow that
// header then fail the preflight with a "CORS error" before the GET runs.
// Keeping the requests "simple" avoids the preflight entirely.

// Returns null when the event is unsupported (caller falls back to legacy UI).
// On success returns the risk-story entities plus an event-level summary so the
// modal can render its risk content straight from the Risk Story API.
export const fetchRiskStory = async (
  internalEventId: string,
): Promise<RiskStoryModel | null> => {
  if (!internalEventId) return null;
  try {
    const res = await axios.get(RISK_STORY_URL(internalEventId));
    const entities = mapRiskStoryResponse(res.data);
    if (!entities || entities.length === 0) return null;
    return { entities, summary: mapRiskStorySummary(res.data) };
  } catch (_) {
    return null;
  }
};

// Enriches entities with current trust/familiarity + audit log. Never throws.
export const enrichEntities = async (
  entities: RiskStoryEntity[],
): Promise<RiskStoryEntity[]> => {
  return Promise.all(
    entities.map(async (entity) => {
      // labelId (the entity's virtualLabelId) is required by the endpoint; without
      // it the request would 400, so skip enrichment and keep risk-story values.
      if (!entity.virtualLabelId) return entity;
      try {
        const res = await axios.get(
          AGGREGATION_URL(entity.entityId, entity.virtualLabelId),
        );
        const agg = mapAggregationResponse(res.data);
        return {
          ...entity,
          currentTrust: agg.currentTrust ?? entity.currentTrust,
          currentFamiliarity:
            agg.currentFamiliarity ?? entity.currentFamiliarity,
          hasTrustChange:
            (agg.currentTrust ?? entity.currentTrust) !== entity.eventTimeTrust,
          auditLog: agg.auditLog,
        };
      } catch (_) {
        return entity; // keep risk-story-derived values on failure
      }
    }),
  );
};
